"use client";

import { useLanguage } from '@/lib/i18n/LanguageContext';
import { Language } from '@/lib/translations';
import { ChevronDown } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';

const languages = {
  uk: 'UA',
  en: 'EN',
} as const;

export default function LanguageSwitcher() {
  const { language, setLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleLanguageChange = (lang: Language) => {
    setLanguage(lang);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-h-[36px] sm:min-h-[40px]"
        aria-label="Select language"
        aria-expanded={isOpen}
      >
        {languages[language]}
        <ChevronDown className={`w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-32 bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden z-50">
          {Object.entries(languages).map(([lang, label]) => (
            <Button
              key={lang}
              variant="ghost"
              onClick={() => handleLanguageChange(lang as Language)}
              className={`w-full px-4 py-2 text-left text-sm transition-colors h-auto justify-start rounded-none ${
                language === lang
                  ? 'bg-blue-50 text-blue-600 font-semibold'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
              aria-label={lang === 'uk' ? 'Українська' : 'English'}
            >
              {label}
              {language === lang && (
                <span className="ml-2 text-blue-600">✓</span>
              )}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}